import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../Utils/AuthContext";
import { StockMarketGameFunctions } from "../Utils/SFunctions";
import "bootstrap/dist/css/bootstrap.min.css";
import { Row, Col, Container, Button, Table, Modal, Form } from "react-bootstrap";

function StockMarketGame() {
  const { name, email } = useAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pathName, setPathName] = useState("");
  const [portfolio, setPortfolio] = useState([]);
  const [userBalance, setUserBalance] = useState(0);
  const [stockSymbol, setStockSymbol] = useState("");
  const [stockDetails, setStockDetails] = useState([]);
  const [quantity, setQuantity] = useState(0);
  const [error, setError] = useState("");
  const [modalType, setModalType] = useState(null); // 'buy' or 'sell'
  const [selectedStock, setSelectedStock] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [transactionsPerPage, setTransactionsPerPage] = useState(10);
  const [transactionError, setTransactionError] = useState("");
  const [totalEquity, setTotalEquity] = useState(0);
  const [netEquityGain, setNetEquityGain] = useState(0);
  const [stockValue, setStockValue] = useState(null);

  const { loadPortfolio, loadUser, getStockPrice, buyStock, sellStock, loadUserTransactions } = StockMarketGameFunctions({ isAuthenticated, email, name, pathName });
  const navigate = useNavigate();
  const routerLocation = useLocation();

  useEffect(() => {
    if (email && name) {
      setIsAuthenticated(true);
      setPathName(routerLocation.pathname);
    } else {
      setIsAuthenticated(false);
      setPathName(routerLocation.pathname);
    }
  }, [email, name, routerLocation.pathname]);

  useEffect(() => {
    if (isAuthenticated) {
      loadUserTransactions()
        .then((data) => {
          setTransactions(data);
          setTransactionError("");
        })
        .catch((err) => {
          setTransactionError("Error loading transactions. Please try again later.");
          setTransactions([]);
        });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      loadUser()
        .then((response) => {
          if (response && response.length > 0) {
            const user = response[0];
            if (user) {
              setUserBalance(user.balance);
            } else {
              setError("User data not found");
            }
          } else {
            setError("No user data available.");
          }
        })
        .catch((err) => setError(err.message || "Error loading user data"));
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      loadPortfolio()
        .then(async (portfolioData) => {
          let totalStockValue = 0;

          const updatedPortfolio = await Promise.all(
            portfolioData.map(async (stock) => {
              try {
                const stockPriceData = await getStockPrice(stock.stock_symbol);
                const stockValue = Number(stockPriceData?.price);
                totalStockValue += stockValue * stock.quantity;
                return { ...stock, current_price: Number(stockPriceData?.price)};
              } catch (error) {
                console.error(`Failed to fetch price for ${stock.stock_symbol}:`, error);
                return { ...stock, current_price: null };
              }
            })
          );

          setPortfolio(updatedPortfolio);
          setStockValue(totalStockValue);
        })
        .catch((err) => setError(err.message || "Error loading portfolio"));
    }
  }, [isAuthenticated]);


  useEffect(() => {
    if (userBalance !== null && stockValue !== null) {
      setTotalEquity(() => {
        const updatedEquity = parseFloat(userBalance) + parseFloat(stockValue);
        return updatedEquity;
      });

      setNetEquityGain(() => {
        const updatedNetEquityGain = parseFloat(userBalance) + parseFloat(stockValue) - 100000;
        return updatedNetEquityGain;
      });
    }
  }, [userBalance, stockValue]);

  const paginateTransactions = () => {
    const indexOfLast = currentPage * transactionsPerPage;
    const indexOfFirst = indexOfLast - transactionsPerPage;
    return transactions.slice(indexOfFirst, indexOfLast);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSignIn = () => {
    navigate("/login", { state: { from: routerLocation.pathname } });
  };

  const handleSearchStock = () => {
    getStockPrice(stockSymbol)
      .then((data) => setStockDetails([data]))
      .catch((err) => alert(err));
  };

  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const resetModal = () => {
    setModalType(null);
    setQuantity(0);
    setIsConfirmed(false);
    setIsExpired(false);
    setCurrentPrice(null);
    setIsProcessing(false);
  };

  useEffect(() => {
    let timer;
    if (modalType) {
      timer = setTimeout(handleTimeout, 60000);
    }

    return () => clearTimeout(timer);
  }, [modalType]);

  const handleTimeout = () => {
    setIsExpired(true);
    alert("Your session has expired. Please try again.");
    resetModal();
  };

  const handleBuyStock = async () => {
    if (isConfirmed) {
      setIsProcessing(true);
      const stock = stockDetails[0];
      try {
        const totalCost = currentPrice * quantity;
        if (totalCost > userBalance) {
          alert("Not enough cash available.");
        } else {
          await buyStock(stock.ticker, quantity, currentPrice);
          alert("Stock purchased successfully.");
          resetModal();
          window.location.reload();
        }
      } catch (err) {
        alert(err.message || "Error buying stock");
        resetModal();
        window.location.reload();
      }
      finally {
        setIsProcessing(false);
      }
    } else {
      const data = await getStockPrice(stockDetails[0].ticker);
      setCurrentPrice(data?.price);
      setIsConfirmed(true);
    }
  };

  const handleSellStock = async () => {
    if (isConfirmed) {
      setIsProcessing(true);
      if (quantity > selectedStock.quantity) {
        alert("Quantity exceeds available stock.");
      } else {
        try {
          await sellStock(selectedStock.stock_symbol, quantity, currentPrice);
          alert("Stock sold successfully.");
          resetModal();
          window.location.reload();
        } catch (err) {
          alert(err.message || "Error selling stock");
          resetModal();
          window.location.reload();
        }
        finally {
          setIsProcessing(false);
        }
      }
    } else {
      const data = await getStockPrice(selectedStock.stock_symbol);
      setCurrentPrice(data?.price);
      setIsConfirmed(true);
    }
  };

  const openBuyModal = () => setModalType("buy");
  const openSellModal = (stock) => {
    setSelectedStock(stock);
    setModalType("sell");
  };

  return (
    <div style={{ textAlign: "left", marginTop: "20px", marginLeft:'40px' }}>
      {isAuthenticated ? (
        <Container>
          <h3>Welcome to the Stock Market Game!</h3>
          <p>Explore your portfolio, track the market, and sharpen your investment skills.</p>

          {/* Metrics Display */}
          <Row className="mt-4">
            <Col md={4}>
              <div className="metric-box">
                <h6>Total Equity</h6>
                <p>${parseFloat(totalEquity || 0).toFixed(2)}</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="metric-box">
                <h6>Net Equity Gain</h6>
                <p>${parseFloat(netEquityGain || 0).toFixed(2)}</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="metric-box">
                <h6>Cash Balance</h6>
                <p>${parseFloat(userBalance || 0).toFixed(2)}</p>
              </div>
            </Col>
          </Row>
          <p>&nbsp;</p>
          <Table style={{ width: "100%", margin: "auto", border: "1px solid #ccc", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th className="text-left" colSpan="2" style={{ borderBottom: "0px solid #ccc", padding: "10px" }}>
                  If you want to purchase a stock, please search for the stock first.
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
              <td style={{ textAlign: "left", padding: "10px" }}>
                <Row className="justify-content-left">
                  <Col md={6} className="d-flex align-items-center">
                    <Form.Control
                      type="text"
                      placeholder="Enter Stock Symbol"
                      value={stockSymbol}
                      onChange={(e) => setStockSymbol(e.target.value)}
                      className="me-2"
                    />
                    <Button
                      onClick={handleSearchStock}
                      style={{ backgroundColor: "#4CAF50", whiteSpace: "nowrap" }}
                    >
                      Search
                    </Button>
                    {/* Lookup Link */}
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          const url = "https://finance.yahoo.com/lookup/";
                          const width = 800;
                          const height = 600;
                          const left = (window.innerWidth - width) / 2;
                          const top = (window.innerHeight - height) / 2;
                          window.open(url, "YahooLookup", `width=${width},height=${height},top=${top},left=${left},resizable=yes`);
                        }}
                        style={{
                          marginLeft: "10px",
                          backgroundColor: "#4CAF50",
                          color: "#fff",
                          padding: "5px 10px",
                          borderRadius: "4px",
                          textDecoration: "none",
                          display: "inline-flex",
                          alignItems: "center",
                          whiteSpace: "nowrap"
                        }}
                      >
                        Ticker Lookup
                    </a>
                  </Col>
                </Row>
              </td>
              </tr>
            </tbody>
          </Table>


          {stockDetails.length > 0 && (
            <Table striped bordered hover className="mt-2" size="sm">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Company Name</th>
                  <th>Last Price</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {stockDetails.map((stock) => (
                  <tr key={stock.ticker}>
                    <td>{stock.ticker}</td>
                    <td>{stock.name}</td>
                    <td>${stock.price.toFixed(2)}</td>
                    <td>
                      <Button
                        onClick={openBuyModal}
                        size="sm"
                        style={{ backgroundColor: "#4CAF50", width: "60px", whiteSpace: "nowrap" }}
                      >
                        Buy
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {/* Portfolio Table */}
          <p>&nbsp;</p>
          <h5 style={{ width: "100%", fontSize: "medium", fontWeight: "bold" }}>Current Portfolio:</h5>
          <Table striped bordered hover className="mt-2" size="sm">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Quantity</th>
                <th>Avg Buy Price</th>
                <th>Current Price</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody style={{ fontSize: "small" }}>
              {portfolio.length === 0 ? (
                <tr>
                  <td colSpan="5">No portfolio found. Start adding stocks to your portfolio!</td>
                </tr>
              ) : (
                portfolio.map((stock) => (
                  <tr key={stock.stock_symbol}>
                    <td>{stock.stock_symbol}</td>
                    <td>{stock.quantity}</td>
                    <td>${parseFloat(stock.avg_buy_price).toFixed(2)}</td>
                    <td>
                      {stock.current_price != null ? (
                      `$${stock.current_price.toFixed(2)}`
                      ) : (
                      "Loading..."
                      )}
                    </td>
                    <td>
                      <Button
                        onClick={() => openSellModal(stock)}
                        size="sm"
                        style={{ backgroundColor: "#4CAF50", width: "60px", whiteSpace: "nowrap" }}
                      >
                        Sell
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>


          {/* Error Message */}
          {error && <p style={{ color: "red" }}>{error}</p>}

          <p>&nbsp;</p>
          <h5 style={{ width: "100%", fontSize: "medium", fontWeight: "bold" }}>
            History of Stock Purchases and Sales:
          </h5>
          <Table striped bordered hover className="mt-2" size="sm">
            <thead>
              <tr>
                <th>Stock Symbol</th>
                <th>Quantity</th>
                <th>Txn Type</th>
                <th>Price</th>
                <th>Txn Date</th>
              </tr>
            </thead>
            <tbody style={{ fontSize: "small" }}>
              {transactionError ? (
                <tr>
                  <td colSpan="5">
                    <p style={{ color: "red" }}>{transactionError}</p>
                  </td>
                </tr>
              ) : (
                <>
                  {transactions.length === 0 ? (
                    <tr>
                      <td colSpan="5">
                        <p>No transactions available.</p>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {paginateTransactions().map((transaction, index) => (
                        <tr key={index}>
                          <td>{transaction.stock_symbol}</td>
                          <td>{transaction.quantity}</td>
                          <td>{transaction.transaction_type}</td>
                          <td>${parseFloat(transaction.price || 0).toFixed(2)}</td>
                          <td>
                            {new Date(transaction.transaction_date)
                              .toISOString()
                              .replace("T", " ")
                              .slice(0, 19)}
                          </td>
                        </tr>
                      ))}
                    {/* Pagination Controls */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "1px",
                        flexWrap: "wrap",
                        padding: "1px",
                      }}
                    >
                      <Button
                        style={{ backgroundColor: "#4CAF50", width: "60px" }}
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        Prev
                      </Button>
                      <span style={{ fontSize: "14px" }}>
                        Page {currentPage} of {Math.ceil(transactions.length / transactionsPerPage)}
                      </span>
                      <Button
                        style={{ backgroundColor: "#4CAF50", width: "60px" }}
                        disabled={
                          currentPage === Math.ceil(transactions.length / transactionsPerPage)
                        }
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        Next
                      </Button>
                    </div>
                    </>
                  )}
                </>
              )}
            </tbody>
          </Table>


          <Modal show={!!modalType} onHide={resetModal}>
            <Modal.Header closeButton>
              <Modal.Title>{modalType === "buy" ? "Buy Stock" : "Sell Stock"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group>
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Number(e.target.value))}
                  disabled={isExpired || isProcessing}
                />
              </Form.Group>

              {isConfirmed && currentPrice && (
                <div style={{ color: "green", fontWeight: "bold" }}>
                  {modalType === "buy" ? (
                    `The current price of ${stockDetails[0]?.ticker} is $${currentPrice.toFixed(2)}. Do you want to buy ${quantity} shares at this price for a total cost of $${(currentPrice * quantity).toFixed(2)}?`
                  ) : (
                    `The current price of ${selectedStock.stock_symbol} is $${currentPrice.toFixed(2)}. Do you want to sell ${quantity} shares at this price?`
                  )}
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                style={{ backgroundColor: "#4CAF50", whiteSpace: "nowrap" }}
                variant="secondary"
                onClick={resetModal}
                disabled={isExpired || isProcessing}
              >
                Cancel
              </Button>
              <Button
                style={{ backgroundColor: "#4CAF50", whiteSpace: "nowrap" }}
                variant="primary"
                onClick={modalType === "buy" ? handleBuyStock : handleSellStock}
                disabled={isExpired || isProcessing}
              >
                {isConfirmed ? "Confirm Again" : "Confirm"}
              </Button>
            </Modal.Footer>
          </Modal>
        </Container>
      ) : (
        <div>
          <h1>Welcome to the Stock Market Game!</h1>
          <p>
            Ready to dive into the exciting world of finances and investments? <br />
            <strong>Sign in</strong> to access your dashboard, track your portfolio, and start learning how to make smart investment decisions.
          </p>

          <Button
            onClick={handleSignIn}
            style={{ backgroundColor: "#4CAF50", border: "none" }}
          >
            Sign In Now!
          </Button>
          <p>&nbsp;</p>
          <p>&nbsp;</p>
        </div>
      )}
    </div>
  );
}

export default StockMarketGame;
